import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Settings, Bell, Shield, Palette, Key, Eye, EyeOff, 
  Check, X, AlertCircle, Loader2, CheckCircle, Mail, Info
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { SecuritySettings } from '../../components/SecuritySettings';
import { getEnabledThemes, applyTheme, getActiveTheme } from '../../config/themes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Password strength rules
const passwordRules = [
  { id: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { id: 'uppercase', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lowercase', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'number', label: 'One number', test: (p) => /\d/.test(p) },
  { id: 'special', label: 'One special character', test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
];

export function AccountSettings() {
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  
  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  
  // Theme state
  const [selectedTheme, setSelectedTheme] = useState(() => getActiveTheme().id);
  const enabledThemes = getEnabledThemes();

  const handleThemeSelect = (themeId) => {
    setSelectedTheme(themeId);
    applyTheme(themeId);
  };
  
  // Email preferences state
  const [emailPrefs, setEmailPrefs] = useState({
    email_enabled: false,
    email_critical_only: true,
    email_service_available: false
  });
  const [emailPrefsLoading, setEmailPrefsLoading] = useState(true);
  const [emailPrefsSaving, setEmailPrefsSaving] = useState(false);
  const [emailPrefsSuccess, setEmailPrefsSuccess] = useState('');

  // Fetch email preferences on mount
  useEffect(() => {
    const fetchEmailPrefs = async () => {
      try {
        const response = await fetch(`${API_URL}/api/in-app-notifications/email-preferences`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setEmailPrefs(data);
        }
      } catch (err) {
        console.error('Failed to fetch email preferences:', err);
      } finally {
        setEmailPrefsLoading(false);
      }
    };
    
    if (token) {
      fetchEmailPrefs();
    }
  }, [token]);

  // Update email preferences
  const updateEmailPrefs = async (field, value) => {
    try {
      setEmailPrefsSaving(true);
      setEmailPrefsSuccess('');
      
      const response = await fetch(`${API_URL}/api/in-app-notifications/email-preferences`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [field]: value })
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmailPrefs(data);
        setEmailPrefsSuccess('Saved');
        setTimeout(() => setEmailPrefsSuccess(''), 2000);
      }
    } catch (err) {
      console.error('Failed to save email preferences:', err);
    } finally {
      setEmailPrefsSaving(false);
    }
  };

  // Check password strength
  const getPasswordStrength = () => {
    if (!newPassword) return { score: 0, label: '', color: '' };
    
    const passedRules = passwordRules.filter(rule => rule.test(newPassword)).length;
    
    if (passedRules <= 2) return { score: passedRules, label: 'Weak', color: 'var(--status-danger)' };
    if (passedRules <= 3) return { score: passedRules, label: 'Fair', color: 'var(--status-warning)' };
    if (passedRules <= 4) return { score: passedRules, label: 'Good', color: 'var(--status-info)' };
    return { score: passedRules, label: 'Strong', color: 'var(--status-success)' };
  };

  const passwordStrength = getPasswordStrength();
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
  const allRulesPassed = passwordRules.every(rule => rule.test(newPassword));
  const canSubmit = currentPassword && allRulesPassed && passwordsMatch && !passwordLoading;

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    if (!canSubmit) return;
    
    setPasswordLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setPasswordSuccess(data.message || 'Password changed successfully!');
        // Clear form
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        
        // Logout and redirect after short delay
        setTimeout(async () => {
          await logout();
          navigate('/login');
        }, 2000);
      } else {
        setPasswordError(data.detail || 'Failed to change password. Please try again.');
      }
    } catch (error) {
      console.error('Password change error:', error);
      setPasswordError('Network error. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const resetPasswordForm = () => {
    setShowPasswordForm(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
  };

  return (
    <div className="page" data-testid="account-settings-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Account Settings</h1>
          <p>Configure your account preferences</p>
        </div>
      </div>

      <div className="settings-sections" style={{ maxWidth: '700px' }}>
        {/* Notifications Section */}
        <div className="glass-panel" style={{ marginBottom: 'var(--space-lg)' }} data-testid="notifications-section">
          <div className="glass-panel-header">
            <div className="glass-panel-title">
              <Bell size={18} className="icon" />
              Notifications
            </div>
            {emailPrefsSuccess && (
              <span style={{ 
                fontSize: '0.75rem', 
                color: 'var(--status-success)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}>
                <Check size={14} />
                {emailPrefsSuccess}
              </span>
            )}
          </div>
          
          {/* Email Notifications Toggle */}
          <div className="settings-option">
            <div>
              <div className="settings-option-label">
                <Mail size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Email Notifications
              </div>
              <div className="settings-option-desc">
                Receive email alerts for critical events (opt-in, off by default)
              </div>
            </div>
            {emailPrefsLoading ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <label className="toggle">
                <input 
                  type="checkbox" 
                  checked={emailPrefs.email_enabled}
                  onChange={(e) => updateEmailPrefs('email_enabled', e.target.checked)}
                  disabled={emailPrefsSaving}
                  data-testid="email-notifications-toggle"
                />
                <span className="toggle-slider"></span>
              </label>
            )}
          </div>
          
          {/* Critical Only Option (only shown if email enabled) */}
          {emailPrefs.email_enabled && (
            <div className="settings-option" style={{ 
              borderTop: '1px solid var(--glass-border)',
              paddingLeft: 'calc(var(--space-md) + 1.5rem)'
            }}>
              <div>
                <div className="settings-option-label">Critical Alerts Only</div>
                <div className="settings-option-desc">
                  Only receive emails for urgent items (PO action required, overdue tasks, security events)
                </div>
              </div>
              <label className="toggle">
                <input 
                  type="checkbox" 
                  checked={emailPrefs.email_critical_only}
                  onChange={(e) => updateEmailPrefs('email_critical_only', e.target.checked)}
                  disabled={emailPrefsSaving}
                  data-testid="email-critical-only-toggle"
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          )}
          
          {/* Email Info Box */}
          {emailPrefs.email_enabled && (
            <div style={{
              background: 'rgba(99, 102, 241, 0.1)',
              border: '1px solid rgba(99, 102, 241, 0.2)',
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              margin: '0 1rem 1rem',
              fontSize: '0.8rem',
              color: 'var(--text-secondary)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.5rem'
            }}>
              <Info size={16} style={{ flexShrink: 0, marginTop: '2px', color: 'var(--accent-primary)' }} />
              <div>
                <strong>Critical notifications include:</strong>
                <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1rem' }}>
                  <li>Purchase orders requiring action</li>
                  <li>Tasks overdue by more than 3 days</li>
                  <li>Security events (new logins, password changes)</li>
                </ul>
              </div>
            </div>
          )}
          
          {/* Push Notifications (placeholder for future) */}
          <div className="settings-option" style={{ opacity: 0.5 }}>
            <div>
              <div className="settings-option-label">Push Notifications</div>
              <div className="settings-option-desc">Receive mobile push alerts (coming soon)</div>
            </div>
            <label className="toggle">
              <input type="checkbox" disabled />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>

        {/* Security Section */}
        <div className="glass-panel" style={{ marginBottom: 'var(--space-lg)' }}>
          <div className="glass-panel-header">
            <div className="glass-panel-title">
              <Shield size={18} className="icon" />
              Security
            </div>
          </div>
          
          {/* Change Password */}
          <div className="settings-option" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <div>
                <div className="settings-option-label" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                  <Key size={16} />
                  Change Password
                </div>
                <div className="settings-option-desc">Update your account password</div>
              </div>
              {!showPasswordForm && (
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowPasswordForm(true)}
                  data-testid="change-password-btn"
                >
                  Change
                </button>
              )}
            </div>
            
            {/* Password Change Form */}
            {showPasswordForm && (
              <form onSubmit={handlePasswordChange} style={{ marginTop: 'var(--space-lg)', width: '100%' }}>
                {/* Error Message */}
                {passwordError && (
                  <div style={{
                    padding: 'var(--space-md)',
                    background: 'var(--status-danger-bg)',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: 'var(--space-md)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-sm)'
                  }} data-testid="password-error">
                    <AlertCircle size={18} style={{ color: 'var(--status-danger)', flexShrink: 0 }} />
                    <span style={{ color: 'var(--status-danger)', fontSize: '0.875rem' }}>{passwordError}</span>
                  </div>
                )}
                
                {/* Success Message */}
                {passwordSuccess && (
                  <div style={{
                    padding: 'var(--space-md)',
                    background: 'var(--status-success-bg)',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: 'var(--space-md)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-sm)'
                  }} data-testid="password-success">
                    <CheckCircle size={18} style={{ color: 'var(--status-success)', flexShrink: 0 }} />
                    <span style={{ color: 'var(--status-success)', fontSize: '0.875rem' }}>{passwordSuccess}</span>
                  </div>
                )}

                {/* Current Password */}
                <div style={{ marginBottom: 'var(--space-md)' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-xs)',
                    color: 'var(--text-secondary)',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}>
                    Current Password
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      required
                      data-testid="current-password-input"
                      style={{
                        width: '100%',
                        padding: 'var(--space-md)',
                        paddingRight: 'calc(var(--space-md) * 2 + 18px)',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--glass-border)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--text-primary)',
                        fontSize: '0.9375rem'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      style={{
                        position: 'absolute',
                        right: 'var(--space-md)',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-muted)',
                        cursor: 'pointer',
                        padding: '4px'
                      }}
                    >
                      {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div style={{ marginBottom: 'var(--space-md)' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-xs)',
                    color: 'var(--text-secondary)',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}>
                    New Password
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      required
                      data-testid="new-password-input"
                      style={{
                        width: '100%',
                        padding: 'var(--space-md)',
                        paddingRight: 'calc(var(--space-md) * 2 + 18px)',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--glass-border)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--text-primary)',
                        fontSize: '0.9375rem'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      style={{
                        position: 'absolute',
                        right: 'var(--space-md)',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-muted)',
                        cursor: 'pointer',
                        padding: '4px'
                      }}
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  
                  {/* Password Strength Indicator */}
                  {newPassword && (
                    <div style={{ marginTop: 'var(--space-sm)' }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 'var(--space-sm)',
                        marginBottom: 'var(--space-xs)'
                      }}>
                        <div style={{
                          flex: 1,
                          height: '4px',
                          background: 'var(--glass-border)',
                          borderRadius: '2px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${(passwordStrength.score / 5) * 100}%`,
                            height: '100%',
                            background: passwordStrength.color,
                            transition: 'width 0.3s, background 0.3s'
                          }} />
                        </div>
                        <span style={{ 
                          fontSize: '0.75rem', 
                          color: passwordStrength.color,
                          fontWeight: '500',
                          minWidth: '50px'
                        }}>
                          {passwordStrength.label}
                        </span>
                      </div>
                      
                      {/* Password Rules */}
                      <div className="responsive-grid-2" style={{ 
                        gap: '4px',
                        fontSize: '0.75rem'
                      }}>
                        {passwordRules.map(rule => (
                          <div 
                            key={rule.id}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '4px',
                              color: rule.test(newPassword) ? 'var(--status-success)' : 'var(--text-muted)'
                            }}
                          >
                            {rule.test(newPassword) ? <Check size={12} /> : <X size={12} />}
                            {rule.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div style={{ marginBottom: 'var(--space-lg)' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-xs)',
                    color: 'var(--text-secondary)',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}>
                    Confirm New Password
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      required
                      data-testid="confirm-password-input"
                      style={{
                        width: '100%',
                        padding: 'var(--space-md)',
                        paddingRight: 'calc(var(--space-md) * 2 + 18px)',
                        background: 'var(--bg-secondary)',
                        border: `1px solid ${confirmPassword ? (passwordsMatch ? 'var(--status-success)' : 'var(--status-danger)') : 'var(--glass-border)'}`,
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--text-primary)',
                        fontSize: '0.9375rem'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{
                        position: 'absolute',
                        right: 'var(--space-md)',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-muted)',
                        cursor: 'pointer',
                        padding: '4px'
                      }}
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {confirmPassword && !passwordsMatch && (
                    <div style={{ 
                      marginTop: 'var(--space-xs)', 
                      fontSize: '0.75rem', 
                      color: 'var(--status-danger)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <X size={12} />
                      Passwords do not match
                    </div>
                  )}
                  {confirmPassword && passwordsMatch && (
                    <div style={{ 
                      marginTop: 'var(--space-xs)', 
                      fontSize: '0.75rem', 
                      color: 'var(--status-success)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <Check size={12} />
                      Passwords match
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
                  <button
                    type="button"
                    onClick={resetPasswordForm}
                    className="btn btn-secondary"
                    disabled={passwordLoading}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!canSubmit}
                    data-testid="submit-password-change"
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-sm)' }}
                  >
                    {passwordLoading ? (
                      <>
                        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                        Changing...
                      </>
                    ) : (
                      'Change Password'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Security Section - 2FA and Sessions */}
        <SecuritySettings />

        {/* Appearance Section */}
        <div className="glass-panel">
          <div className="glass-panel-header">
            <div className="glass-panel-title">
              <Palette size={18} className="icon" />
              Appearance
            </div>
          </div>
          <div className="settings-option" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 'var(--space-md)' }}>
            <div>
              <div className="settings-option-label">Theme</div>
              <div className="settings-option-desc">Choose your preferred theme</div>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 'var(--space-sm)'
            }} data-testid="theme-switcher">
              {enabledThemes.map((theme) => (
                <button
                  key={theme.id}
                  onClick={() => handleThemeSelect(theme.id)}
                  data-testid={`theme-option-${theme.id}`}
                  style={{
                    background: selectedTheme === theme.id
                      ? 'var(--glass-bg-hover)'
                      : 'var(--glass-bg)',
                    border: '2px solid',
                    borderColor: selectedTheme === theme.id
                      ? theme.colors.accent
                      : 'var(--glass-border)',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--space-sm) var(--space-md)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s ease',
                    position: 'relative'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: '4px' }}>
                    <div style={{
                      width: '14px',
                      height: '14px',
                      borderRadius: 'var(--radius-full)',
                      background: theme.preview.primary,
                      flexShrink: 0
                    }} />
                    <span style={{
                      color: 'var(--text-primary)',
                      fontSize: '0.8125rem',
                      fontWeight: selectedTheme === theme.id ? '600' : '500'
                    }}>
                      {theme.name}
                    </span>
                    {selectedTheme === theme.id && (
                      <Check size={14} style={{ color: theme.colors.accent, marginLeft: 'auto' }} />
                    )}
                  </div>
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                    {theme.description}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountSettings;
